import React, { type Dispatch, type SetStateAction } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { getWhatnotIntegrations } from "src/store/plaidIntegration/selector";
import {
  getWhatnotIntegration,
  whatnotIntegrationCreate,
} from "src/apiService/modules/whatnot";
import { getWhatnotUser } from "src/utils/extension/whatnot";
import {
  MissingExtensionError,
  OutdatedExtensionError,
} from "src/utils/extension";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import type { DialogState } from "../types";
import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";

import Row from "./Row";

interface WhatnotProps {
  hideInventory: boolean;
  setDialog: Dispatch<SetStateAction<DialogState>>;
}

function Whatnot({ setDialog, hideInventory }: WhatnotProps) {
  const recordEvent = useRecordEvent();
  const whatnotIntegrations = useSelector(getWhatnotIntegrations);
  const handleWhatnotAdd = async () => {
    try {
      const data = await getWhatnotUser();
      setDialog({
        type: "whatnot-confirm",
        props: {
          username: data.username,
          platform: "whatnot",
          onConfirm: async (bankAccountId?: string | undefined) => {
            if (data?.cookies) {
              const existingWhatnotIntegration = await getWhatnotIntegration();
              if (existingWhatnotIntegration?.id) {
                toast.info("That Whatnot account is already connected.");
                return;
              }
              await whatnotIntegrationCreate({
                cookies: data.cookies,
                username: data.username,
                lastSync: new Date().toISOString(),
                bankAccountId,
                whatnotUserId: data.userId,
              });
              recordEvent({
                type: "feature-adoption",
                feature: "integration-added",
                element: "sale-platform-whatnot",
              });
            }
            setDialog({});
          },
        },
      });
    } catch (e) {
      if (e instanceof MissingExtensionError) {
        setDialog({
          type: "whatnot-add-extension",
          props: { platform: "Whatnot" },
        });
        return;
      }
      if (e instanceof OutdatedExtensionError) {
        setDialog({
          type: "whatnot-add-extension",
          props: { platform: "Whatnot", update: true },
        });
        return;
      }
      toast.error((e as Error).message);
    }
  };
  if (whatnotIntegrations.length === 0)
    return (
      <PlatformIntegrationsTableRow
        name="Whatnot"
        platform="Whatnot"
        isRunning={false}
        sync={false}
        inventory={false}
        onImport={handleWhatnotAdd}
        hideInventory={hideInventory}
        showInventoryCheckbox={false}
        onPullDataNow={async () => handleWhatnotAdd()}
        onSyncChanged={handleWhatnotAdd}
        onAdd={handleWhatnotAdd}
      />
    );
  return (
    <>
      {whatnotIntegrations.map((integration) => (
        <Row
          key={integration.id}
          integration={integration}
          hideInventory={hideInventory}
          setDialog={setDialog}
        />
      ))}
    </>
  );
}

export default Whatnot;
